<script setup lang="ts">
import { symbolPayingToken, useExchange } from 'nomadhub/crypto'
import ICalendar from '~/assets/images/events/icon-calendar.svg?component'
import {
  generateEventAppLink,
  generateEventTicketPaymentLink,
} from '~/composables/events/event'
import { useReferrer } from '~/composables/referrer'
import type { EventItemTicket } from '~~/types/types'

definePageMeta({
  key: (route) => route.fullPath,
  // from: https://github.com/nuxt/nuxt/issues/22080
  alias: ['/hubs/:hub_slug?/events/:event_slug'],
})

const { t, n, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const {
  public: { appLink },
} = useRuntimeConfig()
const localeRoute = useLocaleRoute()

const event_slug = computed(() => route.params.event_slug as string)
const hub_slug = computed(() => route.params.hub_slug as string | undefined)
const privateLinkId = computed(() => route.query.p as string | undefined)

// referer data
await useReferrer()

// event data
const queryData = computed(() => ({
  slug: event_slug.value,
  hub: hub_slug.value,
  locale: locale.value,
  privateLinkId: privateLinkId.value,
}))

const { data: apiData } =
  await useNuxtApp().$client.events.findOne.useQuery(queryData)

if (!apiData.value?.data) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}
const pageData = computed(() => ({
  ...apiData.value!.data,
  analytics: {
    ...apiData.value!.data.analytics,
  },
}))

const consumerGtagId = computed(() => pageData.value?.analytics?.gtag)
const consumerPixelId = computed(() => pageData.value?.analytics?.pixel)

const eventId = computed(() => pageData.value.id)

useSeoMeta({
  title: pageData?.value?.name,
  description: pageData?.value?.description,
  ogTitle: pageData?.value?.name,
  ogDescription: pageData?.value?.description,
  ogImage: `${appLink}/up/activities/${pageData.value.id}_${pageData.value.coverImage}.jpg`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/jpeg',
  ogType: 'website',
  twitterTitle: pageData?.value?.name,
  twitterDescription: pageData?.value?.description,
  twitterImage: `${appLink}/up/activities/${pageData.value.id}_${pageData.value.coverImage}.jpg`,
  twitterCard: 'summary_large_image',
})

const { pixel } = useAnalytics()
const consumerPixel = useConsumerMetaPixel({
  id: consumerPixelId,
})
const consumerGtag = useConsumerGoogleAnalytics({
  id: consumerGtagId,
})

const isShowModalRef = ref(false)

/**
 * Category Sub Events with current event
 */
const eventTickets = computed(() => {
  const {
    categorySubEvents,
    id,
    name,
    description,
    nftCollection,
    costCurrency,
  } = toRaw(pageData.value)

  const events = [
    {
      id,
      name,
      description,
      nftCollection,
      costCurrency,
    },
    ...categorySubEvents,
  ]
    .filter((item) => item.nftCollection !== null)
    .filter((item) => !item.private)

  return events as EventItemTicket[] & {
    displayPrice: string
  }
})

const selectedEventTicket = ref(eventTickets?.value[0]?.id)

const mainOrSubEventId = computed(() => {
  return pageData.value.categorySubEvents.length > 0
    ? pageData.value.categorySubEvents[0].id
    : pageData.value.id
})

const paymentAppLink = computed(() =>
  generateEventTicketPaymentLink({
    eventId: mainOrSubEventId.value,
    privateLinkId,
  })
)

const eventAppLink = computed(() =>
  generateEventAppLink({ eventId: mainOrSubEventId.value })
)

//Price
const currencies = computed(() => {
  const currencyArray = eventTickets.value.map((item) => item.costCurrency)
  return [...new Set(currencyArray)]
})
const tokens = computed(() => {
  const tokenArray = eventTickets.value
    .map((item) => {
      if (item.nftCollection) {
        const { chainId } = item.nftCollection.nft_meta
        const payingToken = item.nftCollection.produce_data?.payingToken
        if (payingToken) {
          return symbolPayingToken(chainId, payingToken)
        }
      }
      return ''
    })
    .filter((token) => token !== '')

  return Array.from(new Set(tokenArray))
})

const tickets = ref<(EventItemTicket & { displayPrice?: string })[]>([])

watch(
  () => [currencies.value, tokens.value],
  async () => {
    if (currencies.value.length > 0 && tokens.value.length > 0) {
      const exchange = await useExchange(
        currencies.value as string[],
        tokens.value as string[]
      )

      tickets.value = eventTickets.value.map((item) => {
        if (item.nftCollection) {
          const value = item.nftCollection.produce_data.mintPrice
          const currency = item.costCurrency
          const token = item.nftCollection.produce_data?.payingToken
          const chainId = item.nftCollection.nft_meta.chainId

          if (!exchange?.exchangeCryptoPrice) {
            console.error('Exchange rate function is not initialized')
            return { ...item, displayPrice: '' }
          }

          const tokenSymbol = symbolPayingToken(chainId, token)

          if (!tokenSymbol) {
            console.error('Token symbol not found')
            return { ...item, displayPrice: '' }
          }

          const convertedValue = exchange.exchangeCryptoPrice(
            value,
            currency,
            tokenSymbol
          )

          return {
            ...item,
            displayPrice: n(convertedValue, {
              style: 'currency',
              currency: item.costCurrency,
            }),
          }
        } else {
          return item
        }
      })
    }
  },
  { immediate: true }
)

const detectAndFormatLinks = (value: string) => {
  if (!value) return ''
  value = value.toString()
  const searchValue = /(https?:\/\/[^\s]+)/gim
  const replaceValue =
    '<a href="$&" target="_blank" rel="noopener noreferrer">$&</a>'
  return value.replace(searchValue, replaceValue)
}

const isChainatlasOrTonx = computed(
  () =>
    pageData.value.id === '677e974db8657d1fb132b71d' ||
    pageData.value.id === '677e9f66b8657d1fb132b732' ||
    pageData.value.id === '677ea2d7b8657d1fb132b73c'
)

/**
 * Handle Buy ticket
 */
async function handleBuyTicket(type: 'link' | 'pass') {
  // track event
  pixel?.proxy.fbq('track', 'Lead')
  consumerPixel?.proxy.fbq('track', 'Lead')

  // small delay
  await new Promise((resolve) => setTimeout(resolve, 300))

  switch (type) {
    case 'pass':
      // display modal
      isShowModalRef.value = true
      break
    case 'link':
      window.location.href = paymentAppLink.value
      break
    default:
      break
  }

  return true
}
</script>

<template>
  <div class="flex flex-col mx-auto max-w-245 pt-6 <lg:mx-4">
    <div class="relative" v-if="pageData">
      <NuxtImg
        v-if="pageData?.coverImage?.startsWith('http')"
        :src="pageData.coverImage"
        alt=""
        class="rounded-lg overflow-hidden w-full md:aspect-video object-cover"
      />
      <NuxtImg
        v-else-if="pageData.coverImage"
        alt=""
        provider="belong"
        :src="`/up/activities/${pageData.id}_${pageData.coverImage}.jpg`"
        class="rounded-lg overflow-hidden w-full md:aspect-video object-cover"
      />

      <img
        class="absolute top-0 left-0 right-0 mx-auto text-brand-dark-black-900 w-11.75 h-2.25"
        alt=""
        src="~/assets/images/events/mask-image.svg"
      />
    </div>
    <h1
      class="mt-5 text-[1.375rem] leading-normal tracking-wide text-center <md:text-left"
    >
      {{ pageData.name }}
    </h1>
    <div
      class="mt-5 mx-auto flex flex-row justify-center w-full flex-wrap gap-y-3 gap-x-9.5 <md:justify-start"
    >
      <div class="m-0 flex items-center text-sm tracking-wide">
        <ICalendar class="mr-1.5 min-w-5.5" />

        <ClientOnly>
          <UxRangeDates
            :start="pageData.start"
            :end="pageData.end"
            :local="pageData.online"
          ></UxRangeDates>
        </ClientOnly>
      </div>

      <div
        class="m-0 flex flex-row items-center text-sm tracking-wide"
        v-if="pageData.fullAddress || pageData.online"
      >
        <div class="min-w-5.5">
          <img
            v-if="pageData.fullAddress"
            alt=""
            src="~/assets/images/events/share-location.svg"
            class="mx-auto"
          />
          <img
            v-else
            alt=""
            src="~/assets/images/events/event-icon-online.svg"
            class="mx-auto"
          />
        </div>

        <p class="ml-1.5">
          <template v-if="!pageData.online && pageData.placeName">
            <NuxtLink
              :href="`https://maps.google.com/?q=${pageData.fullAddress}`"
              target="_blank"
              class="underline decoration-[#7540EE] underline-offset-4"
            >
              {{ pageData.placeName
              }}<template v-if="pageData.streetAddress"
                >, {{ pageData.fullAddress }}</template
              >
            </NuxtLink>
          </template>
          <template v-else> {{ t('online') }} </template>
        </p>
      </div>
    </div>
    <div
      v-if="pageData.description"
      class="event-overview prose mt-8 max-w-[60.625rem] <md:mb-10"
    >
      <p class="font-500 tracking-wide text-left text-[#A3A3A3] m-0">
        <template v-if="pageData.slug === 'pinkdira'">
          Item Overview:
        </template>

        <template v-else-if="isChainatlasOrTonx"> Instructions </template>
        <template v-else>
          {{ t('event-overview') }}
        </template>
      </p>
      <p
        class="mt-3.5 whitespace-pre-line"
        v-html="detectAndFormatLinks(pageData.description)"
      ></p>
    </div>
    <div class="custom-hr mt-18 mb-13.5 !bg-[#2E2E2F] <md:hidden" />
    <div class="flex flex-col gap-3.5">
      <NuxtLink
        v-if="
          pageData.link &&
          pageData.eventType === 'event' &&
          pageData.categorySubEvents.length === 0
        "
        :to="pageData.link"
        class="buy-a-ticket bg-[#7540EE] border-[rgba(23,13,48,0.36)]"
      >
        {{ t('join') }}
      </NuxtLink>

      <NuxtLink
        v-if="
          eventTickets.length < 2 &&
          (pageData.passStrategy || pageData.eventType === 'nftCollection')
        "
        :to="paymentAppLink"
        @click.prevent="handleBuyTicket('link')"
        class="buy-a-ticket bg-[#7540EE] border-[rgba(23,13,48,0.36)]"
      >
        <img
          alt=""
          src="~/assets/images/events/icon-nft-ticket.svg"
          class="mr-4"
        />

        <template v-if="pageData.slug === 'pinkdira'"> Buy the Item </template>
        <template v-else> {{ t('buy-a-ticket') }}</template>
      </NuxtLink>

      <template v-if="eventTickets.length > 1">
        <button
          @click="handleBuyTicket('pass')"
          class="buy-a-ticket bg-[#7540EE] border-[rgba(23,13,48,0.36)]"
        >
          <img
            alt=""
            src="~/assets/images/events/icon-nft-ticket.svg"
            class="mr-4"
          />
          {{ t('buy-a-ticket') }}
        </button>

        <ModalSheet v-model="isShowModalRef">
          <ModalContentEventTicketCheckout
            v-model="selectedEventTicket"
            :event-tickets="tickets"
            :privateLinkId="privateLinkId"
          />
        </ModalSheet>
      </template>

      <NuxtLink :to="eventAppLink" class="buy-a-ticket border-[#626262]">
        <img
          alt=""
          src="~/assets/images/events/icon-event-details.svg"
          class="mr-4"
        />
        {{ t('view-event-details') }}
      </NuxtLink>
      <NuxtLink
        v-if="pageData.hubs?.length"
        :to="`${appLink}/hubs/${pageData.hubs[0]}`"
        class="buy-a-ticket border-[#626262]"
      >
        <img
          alt=""
          src="~/assets/images/events/icon-view-community.svg"
          class="mr-4"
        />
        {{ t('view-community') }}
      </NuxtLink>
    </div>
    <img
      alt=""
      src="~/assets/images/events/powered-by-belong.svg"
      class="mt-12 mx-auto mb-14"
    />
    <div class="flex justify-center">
      <NuxtLink :to="localeRoute('/events')">{{
        t('explore-all-events')
      }}</NuxtLink>
    </div>
  </div>
  <SiteFooter />
</template>
